/**
 * SecureEmbed Energy Portal
 *
 * Main application component with routing, layout, and tier-based access control
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  BarChart3,
  Building2,
  Lightbulb,
  FileText,
  Radio,
  Download,
  LogOut,
  ChevronDown,
  Loader2,
  Shield,
  Menu,
  X,
  TrendingUp,
  Search,
  GitBranch,
  Lock,
  BookOpen,
  ExternalLink,
  Upload,
} from 'lucide-react';
import { TierProvider } from './contexts/TierContext';
import { PersonaProvider } from './contexts/PersonaContext';
import TierBadge from './components/TierBadge';
import SubRoleSelector from './components/SubRoleSelector';
import { parseTierFromCognito } from './constants/tiers';

// Import pages
import Analytics from './pages/Analytics';
import SearchPage from './pages/Search';
import Insights from './pages/Insights';
import Provenance from './pages/Provenance';
import Tier2Query from './pages/Tier2Query';
import Tier3Streaming from './pages/Tier3Streaming';
import Export from './pages/Export';
import Partners from './pages/Partners';
import Login from './pages/Login';
import ResearchReferences from './components/ResearchReferences';
import DatastoreBrowser from './pages/DatastoreBrowser';
import {
  VersionBadge,
  performLogout,
  createAuthContext,
  BaseAuthUser,
} from '@secureembed/shared-crm';

// =============================================================================
// AUTH CONTEXT — built from shared-crm factory (Story #2560 Sprint 6 Task #2575)
// =============================================================================

interface AuthUser extends BaseAuthUser {
  email?: string;
  tenant_id?: string;
  role?: string;
  username?: string;
  tier?: number;
  organization?: string;
  groups?: string[];
}

const ANALYSIS_STORAGE_KEYS = {
  authToken: 'authToken',
  idToken: 'idToken',
  refreshToken: 'refreshToken',
  user: 'user',
};

const { AuthProvider, useAuth } = createAuthContext<AuthUser>({
  portalName: 'analysis',
  storageKeys: ANALYSIS_STORAGE_KEYS,
  demoTokenPrefix: 'demo-token-',
  checkIntervalMs: 60_000,
  enrichUserFromIdToken: (user, payload) => {
    if (user.tier) return user;
    const tierClaim = payload['custom:tier'];
    return {
      ...user,
      tier: parseTierFromCognito(typeof tierClaim === 'string' ? tierClaim : undefined),
    };
  },
  fallbackUser: { email: 'user@secureembed.local', tier: 3 },
  onLogout: () => {
    performLogout({
      storageKeys: [
        ANALYSIS_STORAGE_KEYS.authToken,
        ANALYSIS_STORAGE_KEYS.idToken,
        ANALYSIS_STORAGE_KEYS.refreshToken,
        ANALYSIS_STORAGE_KEYS.user,
      ],
    });
  },
});

// =============================================================================
// REACT QUERY CLIENT
// =============================================================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

// =============================================================================
// NAVIGATION
// =============================================================================

interface NavItem {
  name: string;
  href: string;
  icon: React.FC<any>;
  tier: number;
}

const navigation: NavItem[] = [
  { name: 'Search', href: '/search', icon: Search, tier: 1 },
  { name: 'DataStores', href: '/', icon: BarChart3, tier: 1 },
  { name: 'Partners', href: '/partners', icon: Building2, tier: 1 },
  { name: 'Insights', href: '/insights', icon: Lightbulb, tier: 1.5 },
  { name: 'Provenance', href: '/provenance', icon: GitBranch, tier: 1.5 },
  { name: 'Reports', href: '/reports', icon: FileText, tier: 2 },
  { name: 'Export', href: '/export', icon: Download, tier: 2 },
  { name: 'Upload', href: '/upload', icon: Upload, tier: 2 },
  { name: 'Research', href: '/research', icon: BookOpen, tier: 1 },
  { name: 'Streaming', href: '/streaming', icon: Radio, tier: 3 },
];

// =============================================================================
// SIDEBAR
// =============================================================================

const Sidebar: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  userTier: number;
}> = ({ isOpen, onClose, userTier }) => {
  const location = useLocation();

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside className={`
        fixed top-0 left-0 z-50 h-screen bg-[#2B064F] text-white transform transition-all duration-200
        lg:translate-x-0 lg:static lg:z-0 w-64 flex flex-col
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
          <div className="flex items-center space-x-2">
            <TrendingUp className="w-8 h-8 flex-shrink-0" />
            <span className="font-bold text-lg">The Energy Portal</span>
          </div>
          <button onClick={onClose} className="lg:hidden">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-2 flex-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            const locked = userTier < item.tier;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onClose}
                className={`
                  flex items-center justify-between px-3 py-3 rounded-lg mb-1 transition-colors
                  ${isActive
                    ? 'bg-white/20 text-white'
                    : locked
                      ? 'text-white/40 hover:bg-white/5 hover:text-white/60'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'}
                `}
              >
                <div className="flex items-center">
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="ml-3">{item.name}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {locked && <Lock className="w-3.5 h-3.5 text-white/30" />}
                  <TierBadge tier={item.tier} size="sm" />
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Cross-portal link */}
        <div className="px-2 pb-2">
          <a
            href="https://d16wzzm5ybfs7f.cloudfront.net"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between px-3 py-3 rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-colors border border-white/10"
          >
            <div className="flex items-center">
              <Shield className="w-5 h-5 flex-shrink-0" />
              <span className="ml-3 text-sm">Management Dashboard</span>
            </div>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/20 bg-[#1a0436]">
          <div className="flex items-center space-x-3">
            <Shield className="w-5 h-5 text-white/40" />
            <div>
              <p className="text-xs font-medium text-white/60 uppercase tracking-wider">SecureEmbed</p>
              <VersionBadge portal="energy-portal" collapsed={false} />
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

// =============================================================================
// HEADER
// =============================================================================

const Header: React.FC<{ onMenuClick: () => void }> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-user-menu]')) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  const userInitial = user?.email?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase() || 'U';
  const displayName = user?.email || user?.username || 'User';

  return (
    <header className="h-16 bg-white border-b border-gray-200 shadow-sm flex items-center justify-between px-4 lg:px-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
        >
          <Menu className="w-6 h-6 text-gray-600" />
        </button>
        <h1 className="text-xl font-semibold text-gray-800">SecureEmbed Energy Portal</h1>
      </div>

      <div className="flex items-center space-x-3">
        {user?.tier && <TierBadge tier={user.tier} size="md" />}

        {/* User Menu Dropdown */}
        <div className="relative" data-user-menu>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center space-x-2 p-1 pr-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-[#2B064F] flex items-center justify-center text-white font-medium">
              {userInitial}
            </div>
            <span className="hidden sm:inline text-sm font-medium text-gray-700">
              {user?.username === 'admin' ? 'Admin' : 'User'}
            </span>
            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-[#2B064F] flex items-center justify-center text-white font-medium">
                    {userInitial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.role || 'User'}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 py-1">
                <button
                  onClick={logout}
                  className="flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

// =============================================================================
// LAYOUT
// =============================================================================

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();
  const userTier = user?.tier || 3;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        userTier={userTier}
      />

      <div className="flex-1 flex flex-col min-h-screen">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <SubRoleSelector />
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

// =============================================================================
// PROTECTED ROUTE
// =============================================================================

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#2B064F] animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// =============================================================================
// APP COMPONENT
// =============================================================================

const AppInner: React.FC = () => {
  const { user } = useAuth();
  const userTier = user?.tier || 3;

  return (
    <TierProvider tier={userTier}>
      <PersonaProvider>
      <Routes>
        <Route path="/login" element={<Login />} />

        <Route path="/" element={
          <ProtectedRoute>
            <Layout><Analytics /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/search" element={
          <ProtectedRoute>
            <Layout><SearchPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/partners" element={
          <ProtectedRoute>
            <Layout><Partners /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/insights" element={
          <ProtectedRoute>
            <Layout><Insights /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/provenance" element={
          <ProtectedRoute>
            <Layout><Provenance /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute>
            <Layout><Tier2Query /></Layout>
          </ProtectedRoute>
        } />
        {/* Legacy route redirect */}
        <Route path="/query" element={<Navigate to="/reports" replace />} />
        <Route path="/streaming" element={
          <ProtectedRoute>
            <Layout><Tier3Streaming /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/export" element={
          <ProtectedRoute>
            <Layout><Export /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/research" element={
          <ProtectedRoute>
            <Layout><ResearchReferences /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/upload" element={
          <ProtectedRoute>
            <Layout><Analytics /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/datastores/:name/browse" element={
          <ProtectedRoute>
            <Layout><DatastoreBrowser /></Layout>
          </ProtectedRoute>
        } />

        <Route path="*" element={
          <ProtectedRoute>
            <Navigate to="/" replace />
          </ProtectedRoute>
        } />
      </Routes>
      </PersonaProvider>
    </TierProvider>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppInner />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export { useAuth };
export default App;
